import React, { useState } from 'react';
import { useCallbackObservable } from 'react-use-observable';

import { Alert, App, Typography } from 'antd';

import { tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import { sendEventToGA } from 'v2/helpers/analytics';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import type { AnticipationData } from '../AnticipationPeriodModal';

interface SuggestionAnticipationModalProps {
  open: boolean;
  setClose: () => void;
  suggestionAnticipationValue: number;
  loadAnticipationData: () => void;
  anticipationData: AnticipationData;
}

function SuggestionAnticipationModal({
  open,
  setClose,
  suggestionAnticipationValue,
  loadAnticipationData,
  anticipationData
}: SuggestionAnticipationModalProps) {
  const { message } = App.useApp();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [changePeriod] = useCallbackObservable(() => {
    setIsLoading(true);
    const fromSuggestion = true;
    return financialStatementService.setAnticipationPeriod(suggestionAnticipationValue, fromSuggestion).pipe(
      tap(
        () => {
          setIsLoading(false);
          loadAnticipationData();
          message.success(
            `Alteração realizada com sucesso! Sua antecipação automática está configurada para ${suggestionAnticipationValue} dias após crédito em conta.`
          );
        },
        err => {
          setIsLoading(false);
          message.error(typeof err === 'string' ? err : 'Não foi possível alterar o período de antecipação.');
        }
      )
    );
  }, [suggestionAnticipationValue, message, loadAnticipationData]);

  const handleClose = () => {
    if (!showSuccess) {
      sendEventToGA({
        category: 'Novo Extrato',
        action: 'Sugestão de Antecipação',
        label: 'Sugestão de antecipação rejeitada'
      });
    }

    setClose();
  };

  const handleAcceptSuggestion = () => {
    changePeriod();
    setShowSuccess(true);
  };

  const handleSuccessClose = () => {
    setClose();
    setTimeout(() => setShowSuccess(false), 300);
  };

  const textSuggestionButton = () => {
    if (isLoading) return 'Carregando...';
    if (showSuccess) return 'OK';
    return 'Habilitar agora';
  };

  return (
    <Modal
      open={open}
      setClose={handleClose}
      textTitle={!showSuccess ? 'Sugestão para antecipação de valores' : ''}
      textReturnButton='Agora não'
      textProceedButton={textSuggestionButton()}
      size={!showSuccess ? 'sm' : 'xs'}
      hideReturnButton={showSuccess}
      handleProceedClick={!showSuccess ? handleAcceptSuggestion : handleSuccessClose}
    >
      {!showSuccess && (
        <div className='flex flex-col gap-4'>
          <Typography.Paragraph className='mb-0'>
            Com base no seu histórico de antecipações dos últimos 90 dias, vimos que a melhor opção para o seu perfil é
            a antecipação automática a cada
          </Typography.Paragraph>
          <Typography.Title level={3} className='my-0 text-center'>
            {suggestionAnticipationValue} dias
          </Typography.Title>
          {anticipationData?.has_promotional_fee && (
            <Alert
              type='warning'
              showIcon
              message={
                <>
                  Ao continuar, você deixa de ter direito à taxa promocional no valor de{' '}
                  {anticipationData?.custom_fee?.value}% e passa a receber os valores de acordo com o novo prazo
                  configurado e taxa comum de 2.99% + 0.1% por dia antecipado.
                </>
              }
            />
          )}
          <Alert
            type='info'
            showIcon
            message={
              <>
                Os ganhos futuros com data de crédito superior a {suggestionAnticipationValue} dias serão creditados em
                até 24h.
              </>
            }
          />
          <Typography.Paragraph className='mb-0'>
            Deseja habilitar esse período de antecipação automática agora?
          </Typography.Paragraph>
        </div>
      )}

      {showSuccess && (
        <div className='flex flex-col items-center gap-4 text-center'>
          <img width={64} height={63} src='/img/success_icon.svg' alt='Ícone verde de sucesso' />
          <Typography.Text>Parabéns por habilitar a antecipação automática!</Typography.Text>
        </div>
      )}
    </Modal>
  );
}

export default SuggestionAnticipationModal;
